import { Theme, useTheme } from "@/components/theme/themeProvider"
import { useAppStateUser } from "@/modules/auth/store/useAppStateStore"
import { BoxShopPage } from "@/modules/boxShop/page/boxShopPage"
import { BoxShopPageSuspense } from "@/modules/boxShop/page/boxShopPageSuspense"
import { ChatPage } from "@/modules/chat/pages/ChatPage"
import { ChatPageSuspense } from "@/modules/chat/pages/ChatPageSuspense"
import { CoachAdminPage } from "@/modules/coach/page/coachAdminPage"
import { CoachCreationFinalStepPage } from "@/modules/coach/page/coachCreationFinalStepPage"
import { CoachCreationPage } from "@/modules/coach/page/coachCreationPage"
import { CoachDashboardPage } from "@/modules/coach/page/coachDashboardPage"
import { CoachFinancesPage } from "@/modules/coach/page/coachFinancesPage"
import { CoachMembersPage } from "@/modules/coach/page/coachMembersPage"
import { CoachOnboardingPage } from "@/modules/coach/page/coachOnboardingPage"
import { CoachPlanningPage } from "@/modules/coach/page/coachPlanningPage"
import { CoachOffersPage } from "@/modules/coachOffers/page/coachOffersPage"
import { CoachProgrammationPage } from "@/modules/coachProgrammation/page/coachProgrammationPage"
import {
  useCurrentBoxId,
  useCurrentCoachId,
  useCurrentSpaceManagerActions,
} from "@/modules/core/store/useCurrentSpaceManagerStore"
import { BoxPayinLayout } from "@/modules/finances/components/boxPayinLayout"
import { UserPayinLayout } from "@/modules/finances/components/userPayinLayout"
import { PayoutLayout } from "@/modules/finances/financesPayout/components/payoutLayout"
import { PaymentsToValidateLayout } from "@/modules/finances/paymentsToValidate/components/paymentsToValidateLayout"
import { PromoCodeListView } from "@/modules/promoCode/components/promoCodeListView"
import { TemplatePage } from "@/modules/template/page/templatePage"
import { TemplateWeekPage } from "@/modules/template/page/templateWeekPage"
import { AppLayout } from "@modules/app/appLayout"
import { ConfirmEmailPage } from "@modules/auth/page/confirmEmailPage"
import { LoginPage } from "@modules/auth/page/loginPage"
import { RegisterPage } from "@modules/auth/page/registerPage"
import { ResetPasswordPage } from "@modules/auth/page/resetPasswordPage"
import { SendCodePage } from "@modules/auth/page/sendCodePage"
import { SignUpPage } from "@modules/auth/page/signUpPage"
import { WelcomePage } from "@modules/auth/page/welcomePage"
import { BoxFormCreation } from "@modules/box/components/boxCreationForm/boxFormCreation"
import { useBoxs } from "@modules/box/hooks/useBox"
import { BoxOnboardingLiteVersion } from "@modules/box/page/boxOnboardingLiteVersion"
import { BoxOnboardingStripe } from "@modules/box/page/boxOnboardingStripe"
import { BoxOnboardingStripeReturn } from "@modules/box/page/boxOnboardingStripeReturn"
import { BoxSettingsPage } from "@modules/box/page/boxSettingsPage"
import { BoxDashboardPage } from "@modules/boxDashboard/page/boxDashboardPage"
import { BoxOfferPage } from "@modules/boxOffer/pages/boxOfferPage"
import { BoxPlanningPage } from "@modules/boxPlanning/page/boxPlanningPage"
import { BoxProgrammationTemplatePage } from "@modules/boxProgrammation/page/boxProgrammationTemplatePage"
import { BoxProgrammationSessionsPage } from "@modules/boxProgrammationSessions/page/boxProgrammationSessionsPage"
import { ChatAppWrapper } from "@modules/chat/services/chatAppWrapper"
import { SentryContextSync } from "@modules/common/sentry/sentryContextSync"
import { EndUserLicenseAgreementPage } from "@modules/consumerAgreements/pages/endUserLicenseAgreementPage"
import { PrivacyPolicyPage } from "@modules/consumerAgreements/pages/privacyPolicyPage"
import { UseCharterPage } from "@modules/consumerAgreements/pages/useCharterPage"
import { useServicesContext } from "@modules/core/services/services.context"
import { DashboardPage } from "@modules/dashboard/page/DashboardPage"
import { ExplorePage } from "@modules/explore/page/explorePage"
import { BoxFinanceErrorView, BoxFinancesPage } from "@modules/finances/page/boxFinancesPage"
import { UserFinancesPage } from "@modules/finances/page/userFinancesPage"
import { MembersPage } from "@modules/members/pages/membersPage"
import { RouteNavigate } from "@modules/navigation/hooks/useBaseNavigate"
import { getBoxPath } from "@modules/navigation/hooks/useBoxNavigate"
import { getMySpacePath } from "@modules/navigation/hooks/useMySpaceNavigate"
import { Path } from "@modules/navigation/routes"
import { ProfilePage } from "@modules/profile/myProfile/pages/profilePage"
import { ProgramDetailsPage } from "@modules/programs/page/programDetailsPage"
import { ProgramType } from "@modules/programs/types/programTypes"
import { HUSidebarProvider } from "@modules/sidebar/context/sidebarContext"
import { LocalStorageKeys } from "@modules/storage/localStorageKeys"
import { LogoutPage } from "@/modules/auth/page/logoutPage"
import { GlobalErrorBoundary } from "@/modules/navigation/globalErrorBoundary"
import { TrainingPage } from "@modules/training/page/trainingPage"
import { useQueryClient } from "@tanstack/react-query"
import { Suspense, useCallback, useEffect, useEffectEvent, useState } from "react"
import { ErrorBoundary } from "react-error-boundary"
import { BrowserRouter, Navigate, Outlet, Route, Routes, useParams, useSearchParams } from "react-router"

export const WebNavigation = () => {
  return (
    <BrowserRouter>
      <SentryContextSync />
      <GlobalErrorBoundary>
        <Routes>
          <Route path="/index.html" element={<Navigate to="/" replace />} />
          <Route path="/" element={<SpaceRedirection />} />
          <Route element={<AuthWrapper loggedOut />}>
            <Route path={Path.ConfirmEmail} element={<ConfirmEmailPage />} />
            <Route path={Path.Welcome} element={<WelcomePage />} />
            <Route path={Path.Login} element={<LoginPage />} />
            <Route path={Path.SignUp} element={<SignUpPage />} />
            <Route path={Path.Register} element={<RegisterPage />} />
            <Route path={Path.SendCode} element={<SendCodePage />} />
            <Route path={Path.ResetPassword} element={<ResetPasswordPage />} />
          </Route>
          <Route element={<AuthWrapper />}>
            <Route path={Path.MySpace.Root} element={<SpaceWrapper />}>
              <Route path="" element={<DashboardPage />} />
              <Route path={Path.MySpace.Finances.Root} element={<UserFinancesPage />}>
                <Route path="" element={<UserPayinLayout />} />
                <Route path={Path.MySpace.Finances.Payouts} element={<PayoutLayout />} />
                <Route path={Path.MySpace.Finances.PaymentsToValidate} element={<PaymentsToValidateLayout />} />
                <Route path={Path.MySpace.Finances.PromoCodes} element={<PromoCodeListView />} />
              </Route>
              <Route path={Path.MySpace.Explore} element={<ExplorePage />} />
              <Route path={Path.MySpace.Profile} element={<ProfilePage />} />
              <Route path={Path.MySpace.Training} element={<TrainingPage />} />
              <Route path={Path.MySpace.Templates} element={<TemplatePage />} />
              <Route path={Path.MySpace.ProgramDetails(":programId")} element={<ProgramDetailsPage />} />
              <Route path={Path.MySpace.ProgramOnOffDetails(":programId")} element={<ProgramDetailsPage />} />
              <Route path={Path.MySpace.TemplateWeek(":templateId")} element={<TemplateWeekPage />} />
              <Route
                path={Path.MySpace.ProgramSubscribingPage(":programId")}
                element={<ProgramDetailsPage isSubscribingPage />}
              />
              <Route
                path={Path.MySpace.ProgramOnOffSubscribingPage(":programId")}
                element={<ProgramDetailsPage isSubscribingPage />}
              />
              <Route path={Path.MySpace.Programs} element={<ProgramDetailsPage />} />
              <Route
                path={Path.Chat.Root}
                element={
                  <Suspense fallback={<ChatPageSuspense />}>
                    <ChatPage />
                  </Suspense>
                }
              />
            </Route>
            <Route path={Path.Box.Root(":boxId")} element={<SpaceWrapper />}>
              <Route path="" element={<Navigate to={Path.Box.Dashboard} replace />} />
              <Route
                path={Path.Box.Dashboard}
                element={
                  <ErrorBoundary fallback={<BoxFinanceErrorView />}>
                    <BoxDashboardPage />
                  </ErrorBoundary>
                }
              />
              <Route path={Path.Box.Finances.Root} element={<BoxFinancesPage />}>
                <Route path="" element={<BoxPayinLayout />} />
                <Route path={Path.Box.Finances.Payouts} element={<PayoutLayout />} />
                <Route path={Path.Box.Finances.PaymentsToValidate} element={<PaymentsToValidateLayout />} />
                <Route path={Path.Box.Finances.PromoCodes} element={<PromoCodeListView />} />
              </Route>
              <Route path={Path.Box.Profile} element={<ProfilePage />} />
              <Route path={Path.Box.Members} element={<MembersPage />} />
              <Route path={Path.Box.Offers} element={<BoxOfferPage />} />
              <Route path={Path.Box.Admin} element={<BoxSettingsPage />} />
              <Route path={Path.Chat.Root} element={<ChatPage />} />
              <Route path={Path.Box.Return} element={<BoxOnboardingStripeReturn />} />
              <Route path={Path.Box.Refresh} element={<OnboardingRedirection />} />
              <Route path={Path.Box.Planning} element={<BoxPlanningPage />} />
              <Route
                path={Path.Box.Shop}
                element={
                  <Suspense fallback={<BoxShopPageSuspense />}>
                    <BoxShopPage />
                  </Suspense>
                }
              />
              <Route path={Path.Box.OnboardingStripe} element={<BoxOnboardingStripe />} />
              <Route path={Path.Box.LiteVersion} element={<BoxOnboardingLiteVersion />} />
              <Route path={Path.Box.ProgrammationDetails(":boxProgramId")} element={<BoxPlanningPage />} />
              <Route
                path={Path.Box.ProgrammationTemplate(":boxProgramId")}
                element={<BoxProgrammationTemplatePage />}
              />
              <Route
                path={Path.Box.ProgrammationSessions(":boxProgramId")}
                element={<BoxProgrammationSessionsPage />}
              />
            </Route>
            <Route path={Path.CreateBox} element={<BoxFormCreation />} />
            <Route path={Path.CoachOnboarding} element={<CoachOnboardingPage />} />
            <Route path={Path.CreateCoach} element={<CoachCreationPage />} />
            <Route path={Path.CreateCoachComplete} element={<CoachCreationFinalStepPage />} />
            <Route path={Path.Return} element={<OnboardingRedirection />} />
            <Route path={Path.Coach.Root(":coachId")} element={<SpaceWrapper />}>
              <Route path="" element={<Navigate to={Path.Coach.Dashboard} replace />} />
              <Route path={Path.Coach.Dashboard} element={<CoachDashboardPage />} />
              <Route path={Path.Coach.Planning} element={<CoachPlanningPage />} />
              <Route path={Path.Coach.Members} element={<CoachMembersPage />} />
              <Route path={Path.Coach.Programmation} element={<CoachProgrammationPage />} />
              <Route path={Path.Coach.Offers} element={<CoachOffersPage />} />
              <Route path={Path.Coach.Finances} element={<CoachFinancesPage />} />
              <Route path={Path.Coach.Admin} element={<CoachAdminPage />} />
              <Route path={Path.Chat.Root} element={<ChatPage />} />
            </Route>
          </Route>
          <Route path={Path.ProgramInvitation} element={<ProgramInvitationRedirection />} />
          <Route path={Path.ProgramSubscription} element={<ProgramInvitationRedirection />} />
          <Route path={Path.BoxOfferInvitation} element={<BoxOfferInvitationRedirection />} />
          <Route path={Path.ProgramBundleInvitation} element={<ProgramBundleInvitationRedirection />} />
          <Route path={Path.ProgramBundleSubscription} element={<ProgramBundleInvitationRedirection />} />
          <Route path={Path.SharedCoachInvitation} element={<SharedCoachInvitationRedirection />} />
          <Route path={Path.BoxStaffInvitation} element={<BoxStaffInvitationRedirection />} />
          <Route path={Path.BoxInvitation} element={<BoxInvitationRedirection />} />
          <Route path={Path.BoxParticipation} element={<SpaceRedirection />} />
          <Route path={Path.BoxTransaction} element={<BoxTransactionRedirection />} />
          <Route path={Path.Logout} element={<LogoutPage />} />
          <Route element={<ConsumerAgreements />}>
            <Route path={Path.PrivacyPolicy} element={<PrivacyPolicyPage />} />
            <Route path={Path.Eula} element={<EndUserLicenseAgreementPage />} />
            <Route path={Path.UseCharter} element={<UseCharterPage />} />
          </Route>
          <Route path="*" element={<SpaceRedirection />} />
        </Routes>
      </GlobalErrorBoundary>
    </BrowserRouter>
  )
}

const AuthWrapper: React.FC<{ loggedOut?: boolean }> = ({ loggedOut }) => {
  const { setTheme } = useTheme()
  const user = useAppStateUser()
  const isValid = loggedOut ? !user : !!user

  const onLoggedOut = useEffectEvent(() => {
    if (!user) setTheme(Theme.AthleteLight)
  })

  useEffect(() => {
    onLoggedOut()
  }, [loggedOut, user])

  return isValid ? <Outlet /> : <Navigate to={loggedOut ? Path.Root : Path.Welcome} />
}

const SpaceWrapper: React.FC = () => {
  const [rdy, setRdy] = useState(false)
  const params = useParams()
  const { setBoxId, setCoachId } = useCurrentSpaceManagerActions()
  const { setTheme, theme } = useTheme()
  const { data: boxs, isLoading, isRefetching } = useBoxs()
  const isUserBox = !!boxs.find((box) => box.id === params.boxId)
  const boxId = useCurrentBoxId()
  const coachId = useCurrentCoachId()
  const queryClient = useQueryClient()

  const changeSpace = useCallback(
    ({ newBoxId, newCoachId }: { newBoxId: string | null; newCoachId: string | null }) => {
      if (!!newBoxId && !!newCoachId) {
        throw new Error("Cannot change space to both box and coach")
      }

      const resetQueries = () => {
        const keysToKeep = [["boxs"]]
        const keysToKeepStr = new Set(keysToKeep.map((k) => JSON.stringify(k)))
        setTimeout(() => {
          void queryClient.resetQueries({
            predicate: (query) => !keysToKeepStr.has(JSON.stringify(query.queryKey)),
          })
        }, 0) // wait for the queryClient to be initialized (prevents 400 errors during changing space)
      }

      if (newBoxId) {
        if (theme !== Theme.OwnerDark) setTheme(Theme.OwnerDark)

        if (newBoxId !== boxId) {
          setBoxId(newBoxId)
          resetQueries()
        }
      } else if (newCoachId) {
        if (theme !== Theme.CoachLight) setTheme(Theme.CoachLight)

        if (newCoachId !== coachId) {
          setCoachId(newCoachId)
          resetQueries()
        }
      } else if (boxId || coachId) {
        if (theme !== Theme.AthleteLight) setTheme(Theme.AthleteLight)

        setBoxId(null) // will set coachId as null too
        resetQueries()
      }
    },
    [queryClient, boxId, setBoxId, coachId, setCoachId, setTheme, theme],
  )

  useEffect(() => {
    if (!isLoading && !isRefetching) {
      if (params.boxId) {
        if (isUserBox) {
          changeSpace({ newBoxId: params.boxId, newCoachId: null })
        } else {
          changeSpace({ newBoxId: null, newCoachId: null })
        }
      } else if (params.coachId) {
        changeSpace({ newBoxId: null, newCoachId: params.coachId })
      } else {
        changeSpace({ newBoxId: null, newCoachId: null })
      }
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setRdy(true)
    }
  }, [params.boxId, params.coachId, isUserBox, isLoading, isRefetching])

  if (!rdy || isLoading) return null

  if (params.boxId && !isUserBox) {
    return <Navigate to={getMySpacePath(RouteNavigate.Root)} replace />
  }

  return (
    <HUSidebarProvider>
      <ChatAppWrapper>
        <AppLayout>
          <Outlet />
        </AppLayout>
      </ChatAppWrapper>
    </HUSidebarProvider>
  )
}

const SpaceRedirection = () => {
  const currentBoxId = useCurrentBoxId()

  if (currentBoxId) {
    return <Navigate to={getBoxPath(RouteNavigate.Root, { newBoxId: currentBoxId })} replace />
  }
  const programId = localStorage.getItem(LocalStorageKeys.space.redirectionProgramId) ?? ""
  const programType = localStorage.getItem(LocalStorageKeys.space.redirectionProgramType) ?? ""

  if (programId !== "") {
    localStorage.removeItem(LocalStorageKeys.space.redirectionProgramId)
    localStorage.removeItem(LocalStorageKeys.space.redirectionProgramType)

    const targetPath =
      programType === ProgramType.ONOFF.toString()
        ? Path.MySpace.ProgramOnOffDetails(programId)
        : Path.MySpace.ProgramDetails(programId)

    return <Navigate to={getMySpacePath(targetPath)} />
  }

  return <Navigate to={getMySpacePath(RouteNavigate.Root)} replace />
}

const OnboardingRedirection = () => {
  const params = useParams()

  const stripeCoachProOnboardingRedirect = localStorage.getItem(LocalStorageKeys.coach.onboarding.redirect)

  if (stripeCoachProOnboardingRedirect) {
    const coachId = stripeCoachProOnboardingRedirect
    localStorage.removeItem(LocalStorageKeys.coach.onboarding.redirect)
    return <Navigate to={Path.CreateCoachComplete} replace state={{ coachId, stripeReady: true }} />
  }

  if (params.boxId) {
    return <Navigate to={getBoxPath(Path.Box.Admin, { newBoxId: params.boxId })}></Navigate>
  }
  return <SpaceRedirection />
}

const ProgramInvitationRedirection = () => {
  const [searchParams] = useSearchParams()
  const programId = searchParams.get("programId") ?? ""
  const programType = searchParams.get("programType") ?? ""

  // Store the programId and programType in localStorage to redirect for the dynamic link
  localStorage.setItem(LocalStorageKeys.space.redirectionProgramId, programId)
  localStorage.setItem(LocalStorageKeys.space.redirectionProgramType, programType)

  if (programId != "" && programType === ProgramType.ONOFF.toString()) {
    return <Navigate to={getMySpacePath(Path.MySpace.ProgramOnOffDetails(programId))}></Navigate>
  } else if (programId != "" && (programType === ProgramType.VIRTUAL.toString() || !programType)) {
    return <Navigate to={getMySpacePath(Path.MySpace.ProgramDetails(programId))}></Navigate>
  }
  return <SpaceRedirection />
}

const ProgramBundleInvitationRedirection = () => {
  const [searchParams] = useSearchParams()
  const { localStorageService } = useServicesContext()

  const programBundleId = searchParams.get("programBundleId") ?? ""
  const programBundleType = searchParams.get("programType") ?? ""

  // Store the programBundleId in localStorage to redirect for the dynamic link
  localStorageService.save(LocalStorageKeys.programBundleInvitation, { programBundleId, programBundleType })

  if (programBundleId && programBundleId != "") {
    return <Navigate to={Path.MySpace.Root}></Navigate>
  }

  return <SpaceRedirection />
}

const BoxInvitationRedirection = () => {
  const [searchParams] = useSearchParams()
  const { localStorageService } = useServicesContext()

  const boxId = searchParams.get("boxId") ?? ""
  // Store the boxId in localStorage to redirect for the dynamic link
  localStorageService.save(LocalStorageKeys.boxInvitation, { boxId })

  if (boxId && boxId != "") {
    return <Navigate to={Path.MySpace.Root}></Navigate>
  }

  return <SpaceRedirection />
}

const BoxStaffInvitationRedirection = () => {
  const [searchParams] = useSearchParams()
  const { localStorageService } = useServicesContext()

  const boxId = searchParams.get("boxId") ?? ""
  // Store the boxId in localStorage to redirect for the dynamic link
  localStorageService.save(LocalStorageKeys.boxStaffInvitation.boxId, { value: boxId })

  if (boxId && boxId != "") {
    return <Navigate to={Path.MySpace.Root}></Navigate>
  }

  return <SpaceRedirection />
}

const BoxTransactionRedirection = () => {
  const [searchParams] = useSearchParams()
  const { localStorageService } = useServicesContext()

  const boxId = searchParams.get("boxId") ?? ""
  const transactionId = searchParams.get("transactionId") ?? ""

  // Store the transactionId in localStorage to redirect for the dynamic link
  localStorageService.save(LocalStorageKeys.boxTransaction.transactionId, { value: transactionId })

  if (boxId && boxId != "") {
    return <Navigate to={getBoxPath(Path.Box.Finances.Root, { newBoxId: boxId })}></Navigate>
  }
  return <SpaceRedirection />
}

const SharedCoachInvitationRedirection = () => {
  const [searchParams] = useSearchParams()
  const { localStorageService } = useServicesContext()
  const programId = searchParams.get("programId") ?? ""

  // Store the programId and programType in localStorage to redirect for the dynamic link
  localStorageService.save(LocalStorageKeys.sharedCoachInvitation.programId, { value: programId })

  if (programId && programId != "") {
    return <Navigate to={getMySpacePath(RouteNavigate.Root)}></Navigate>
  }

  return <SpaceRedirection />
}

const BoxOfferInvitationRedirection = () => {
  const [searchParams] = useSearchParams()
  const { localStorageService } = useServicesContext()

  const boxOfferId = searchParams.get("boxOfferId") ?? ""
  const boxId = searchParams.get("boxId") ?? ""
  const shortLink = searchParams.get("shortLink") ?? ""

  // Store the boxOfferId in localStorage to redirect for the dynamic link
  localStorageService.save(LocalStorageKeys.boxOfferInvitation.invitation, { boxOfferId, boxId, shortLink })

  if (boxOfferId && boxOfferId != "") {
    return <Navigate to={getMySpacePath(RouteNavigate.Root)}></Navigate>
  }

  return <SpaceRedirection />
}

const ConsumerAgreements: React.FC = () => {
  const user = useAppStateUser()

  return user ? <SpaceWrapper /> : <Outlet />
}
